import { Card, Grid, pn, Stack, Typography, useElementBreakpoints, ValidationErrors } from "@compass/core";
import { DatePicker, Select } from "components";
import { AprRequest, AprRequestFilter, AprResource, LkStudentDisplayFieldId } from "generated/openApi";
import produce from "immer";
import "moment-timezone";

export const AprFilters = (props: {
  filter: AprRequestFilter;
  setFilter: (value: AprRequestFilter) => void;
  resource: AprResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const properties =
    props.resource.properties?.find((x) => x.propertyName === pn<AprRequest>("filter"))?.children || [];
  const reportSchoolYear = props.resource.aprSchoolYears?.find((x) => x.id === props.filter.aprSchoolYearId)?.schoolYear ?? undefined;

  const aprSchoolYearField = () => {
    const propertyName = pn<AprRequestFilter>("aprSchoolYearId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.resource.aprSchoolYears?.find((x) => x.id === props.filter.aprSchoolYearId)}
        options={props.resource.aprSchoolYears}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.aprSchoolYearId = value?.id;
              draft.schoolYear = value?.schoolYear;
            })
          )
        }
      />
    );
  };

  const programField = () => {
    const propertyName = pn<AprRequestFilter>("programId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.resource.programs?.find((x) => x.id === props.filter.programId)}
        options={props.resource.programs}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) => props.setFilter({ ...props.filter, programId: value?.id })}
      />
    );
  };

  const schoolYearField = () => {
    const propertyName = pn<AprRequestFilter>("schoolYear");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        label="School Year"
        required
        disabled={reportSchoolYear !== undefined}
        errors={props.validationErrors[propertyName]}
        value={props.resource.schoolYears?.find((x) => x.id === (reportSchoolYear ?? props.filter.schoolYear))}
        options={props.resource.schoolYears}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) => props.setFilter({ ...props.filter, schoolYear: value?.id })}
      />
    );
  };

  const serviceDateFromField = () => {
    const propertyName = pn<AprRequestFilter>("serviceDateFrom");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.filter.serviceDateFrom}
        onChange={(value) => props.setFilter({ ...props.filter, serviceDateFrom: value })}
      />
    );
  };

  const serviceDateToField = () => {
    const propertyName = pn<AprRequestFilter>("serviceDateTo");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.filter.serviceDateTo}
        onChange={(value) => props.setFilter({ ...props.filter, serviceDateTo: value })}
      />
    );
  };

  return (
    <Card>
      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={smallFieldBreakpoints}>
          {aprSchoolYearField()}
        </Grid>

        {props.resource.displayFields?.find((x) => x.id === LkStudentDisplayFieldId.Program) && (
          <Grid item xs={smallFieldBreakpoints}>
            {programField()}
          </Grid>
        )}

        {props.filter.programId !== undefined && props.filter.programId !== null && (
          <Grid item xs={smallFieldBreakpoints}>
            {schoolYearField()}
          </Grid>
        )}

        {/* Filter out FPR && Interim Report */}
        {props.filter.aprSchoolYearId != 6 && props.filter.aprSchoolYearId != 7 && (
          <Grid item xs={smallFieldBreakpoints}>
            <Stack>
              {serviceDateFromField()}
              <Typography variant="caption">
                Service dates only apply to <b>Section V</b>
              </Typography>
            </Stack>
          </Grid>
        )}

        {/* Filter out FPR && Interim Report */}
        {props.filter.aprSchoolYearId != 6 && props.filter.aprSchoolYearId != 7 && (
          <Grid item xs={smallFieldBreakpoints}>
            {serviceDateToField()}
          </Grid>
        )}
      </Grid>
    </Card>
  );
};
